import { useEffect, useRef } from "react";

/**
 * Run `fetch` once per distinct `key`, and again only when the key changes.
 *
 * Use this instead of guarding an effect on "the list is empty". That guard cannot tell
 * "we have not fetched yet" from "we fetched and got nothing" — so when a request fails
 * or returns an empty list, the effect re-fires the moment `isLoading` flips back, and
 * the two states chase each other into an unbounded request loop.
 *
 * Pass `null` as the key when the fetch should not run yet (an id is not known, a tab is
 * inactive, the data was supplied by the caller).
 *
 * You can also pass `null` when the caller can already see this key's data is loaded (e.g.
 * `deals.length === 0 ? key : null`), to skip refetching — and resetting — a list a user has
 * scrolled through. This is only safe when the store instance is scoped one-to-one with the
 * key the hook is keyed on (a registry/Provider store keyed by the same id, e.g.
 * `getRegularDealsStore(channelId)`). For a store shared across different queries (a
 * module-level singleton reused for unrelated ids, e.g. one global store backing several
 * category tabs), a non-empty list is not evidence that *this* key's data is loaded — it may
 * be a previous key's leftover data — so do not add that guard there; let the hook refetch on
 * every mount instead.
 *
 * Pass `enabled: false` when the underlying store's `isLoading` lock is currently held by
 * an unrelated operation on the same store (some stores share one lock across several
 * fetch methods). While disabled, the attempt is not recorded, so the fetch is retried
 * once the lock clears instead of being silently and permanently skipped. This only rescues
 * a fetch whose lock was taken in an *earlier* render/commit. If two components sharing one
 * store's lock mount in the same passive-effect flush, both read `enabled` from the same
 * pre-lock render and the gate does not help — split the lock for the operation that must
 * not be blocked instead (see `isCategoriesLoading` in `trending-deals.ts`).
 */
export function useFetchOnce(key: string | null, fetch: () => void, enabled = true): void {
  const attemptedKey = useRef<string | null>(null);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  useEffect(() => {
    // Record the key only when the fetch actually starts. These stores share one
    // `isLoading` lock across unrelated operations, so a call made while the lock is
    // held is silently rejected — recording it would strand the fetch forever.
    if (key === null || !enabled || attemptedKey.current === key) {
      return;
    }
    attemptedKey.current = key;
    fetchRef.current();
  }, [key, enabled]);
}
